import { createSelector, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { utils } from '../utils';

// SELECTORS -------------------------------------------

const allNotifications = createSelector(
    state => state.systemNotifications.list,
    notifications => notifications,
);

const selectedNotificationId = createSelector(
    state => state.systemNotifications.selectedNotificationId,
    id => id,
);

const selectedNotification = createSelector(
    allNotifications,
    selectedNotificationId,
    (notifications, notificationId) => notifications.find(n => n.id === notificationId),
);

const systemNotificationsLoading = createSelector(
    state => state.systemNotifications.isLoading,
    isLoading => Object.values(isLoading).some(val => val),
);

const isDeleteDialogOpen = createSelector(
    state => state.systemNotifications.isDeleteDialogOpen,
    isOpen => isOpen,
);

const notificationAlert = createSelector(
    state => state.systemNotifications.alert,
    alert => alert,
);

export const systemNotificationSelectors = {
    allNotifications,
    selectedNotificationId,
    selectedNotification,
    systemNotificationsLoading,
    isDeleteDialogOpen,
    notificationAlert,
};

// ACTIONS -------------------------------------------

const getNotifications = createAsyncThunk(
    'systemNotifications/getNotifications',
    async () => {
        const fn = httpsCallable(getFunctions(), 'systemNotifications-getSystemNotifications_v2');
        return await fn();
    },
);

const createNotification = createAsyncThunk(
    'systemNotifications/createNotification',
    async (notification) => {
        const fn = httpsCallable(getFunctions(), 'systemNotifications-createSystemNotification_v2');
        return await fn(notification);
    },
);

const updateNotification = createAsyncThunk(
    'systemNotifications/updateNotification',
    async (notification) => {
        const fn = httpsCallable(getFunctions(), 'systemNotifications-updateSystemNotification_v2');
        return await fn(notification);
    },
);

const deleteNotification = createAsyncThunk(
    'systemNotifications/deleteNotification',
    async (notificationId) => {
        const fn = httpsCallable(getFunctions(), 'systemNotifications-deleteSystemNotification_v2');
        return await fn(notificationId);
    },
);

// REDUCER SLICE -------------------------------------------

const {
    getUpdatedList,
    trimActionType,
    setAlertError,
    setAlertSuccess,
    alertInitialState,
    removeItemFromListById,
} = utils;

const initialState = {
    list: [],
    selectedNotificationId: null,
    isLoading: {},
    isDeleteDialogOpen: false,
    alert: alertInitialState,
};

export const SystemNotificationSlice = createSlice({
    name: 'systemNotifications',
    initialState,
    reducers: {
        selectNotification(state, action) {
            state.selectedNotificationId = action.payload;
        },
        toggleDeleteDialogOpen(state) {
            state.isDeleteDialogOpen = !state.isDeleteDialogOpen;
        },
        hideAlert(state) {
            state.alert.open = false;
        },
        resetSlice() {
            return initialState;
        },
    },
    extraReducers: {
        [getNotifications.fulfilled]: (state, action) => {
            state.list = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getNotifications.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getNotifications.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [createNotification.fulfilled]: (state, action) => {
            state.list = [...state.list, action.payload.data];
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'Notification created!');
        },
        [createNotification.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [createNotification.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [updateNotification.fulfilled]: (state, action) => {
            state.list = getUpdatedList(state.list, action.payload.data);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'Notification updated!');
        },
        [updateNotification.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [updateNotification.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [deleteNotification.fulfilled]: (state, action) => {
            state.list = removeItemFromListById(state.list, action.payload.data.id);
            state.selectedNotificationId = null;
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'Notification deleted!');
        },
        [deleteNotification.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [deleteNotification.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
    },
});

export const systemNotificationActions = {
    ...SystemNotificationSlice.actions,
    getNotifications,
    createNotification,
    updateNotification,
    deleteNotification,
};

export default SystemNotificationSlice.reducer;
